if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get productData() {
      return window._themeProducts?.[this.productId] || null;
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const isDaysChange = target.closest?.('.grid-1-column-days-source');
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
      const localVariant = this.getVariantFromSelectedOptions();

      this.pendingDaysValue = this.getSelectedDaysValue();

      if (!isDaysChange && this.variantPicker && this.pendingDaysValue) {
        this.positionDaysSliderWhenReady(this.variantPicker, this.pendingDaysValue);
      }

      if (localVariant) {
        this.updatePriceFromLocalVariant(localVariant);
      }

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html) || this.getVariantFromSelectedOptions();

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        // Set before anything below runs: updateQuantityRules() -> updateGridSubtotal()
        // falls back to this.currentVariant when called with no argument, so it must
        // already reflect the newly selected variant or it clobbers the correct
        // subtotal that updatePriceFromLocalVariant(variant) sets a few lines down.
        this.currentVariant = variant;

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        this.updatePriceFromLocalVariant(variant);
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.updateGridSubmitAvailability(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true);

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    getSelectedOptionDisplayValues(root = this.variantPicker) {
      if (!root) return [];

      return Array.from(root.querySelectorAll('[data-picker-field][data-option-name]')).map((field) => {
        const pickerFieldType = field.dataset.pickerField;

        if (pickerFieldType === 'select') {
          const select = field.querySelector('select');
          const selectedOption = select?.selectedOptions?.[0];
          return selectedOption?.value || select?.value || '';
        }

        const checkedInput = field.querySelector('input[type="radio"]:checked')
          || field.querySelector('input[type="radio"]:not([disabled])')
          || field.querySelector('input[type="radio"]');

        return checkedInput?.value || '';
      }).filter(Boolean);
    }

    getVariantFromSelectedOptions(root = this.variantPicker) {
      const productData = this.productData;
      const selectedValues = this.getSelectedOptionDisplayValues(root);

      if (!productData?.variants?.length || !selectedValues.length) return null;

      return productData.variants.find((variant) => {
        if (!Array.isArray(variant.options) || variant.options.length !== selectedValues.length) return false;
        return variant.options.every((optionValue, index) => optionValue === selectedValues[index]);
      }) || null;
    }

    formatVariantPrice(priceInCents) {
      if (typeof formatMoney === 'function' && window.MinimogSettings?.money_format) {
        try {
          return formatMoney(priceInCents, window.MinimogSettings.money_format);
        } catch (error) {}
      }

      if (window.Shopify?.formatMoney) {
        try {
          return window.Shopify.formatMoney(priceInCents, window.MinimogSettings?.money_format);
        } catch (error) {}
      }

      return `${priceInCents}`;
    }

    getCurrentQuantity() {
      const quantityValue = this.quantityInputs
        .map((input) => parseInt(input?.value || '1', 10))
        .find((value) => Number.isFinite(value) && value > 0);

      return quantityValue || 1;
    }

    syncQuantityInputs(sourceInput) {
      const nextValue = String(sourceInput?.value || '1');

      this.quantityInputs.forEach((input) => {
        if (!input || input === sourceInput || input.value === nextValue) return;
        input.value = nextValue;
      });
    }

    updateGridSubtotal(variant = this.currentVariant) {
      const subtotalRoot = this.querySelector('[data-grid-subtotal]');
      const subtotalPrice = subtotalRoot?.querySelector('[data-grid-subtotal-price]');

      if (!subtotalRoot || !subtotalPrice) return;

      if (!variant) {
        subtotalRoot.hidden = true;
        return;
      }

      subtotalRoot.hidden = false;

      // Grid-1-column's merged plan/pack picker pre-checks a default variant so
      // Add to cart works immediately, but visually shows nothing chosen yet
      // (see gridVisualSelectionState in product-variant-picker.liquid). That flag
      // lives on this element (<product-info>) rather than on the inner
      // <variant-picker>, since option changes replace <variant-picker> with a
      // fresh node on every swap. Keep the subtotal consistent with it until the
      // shopper actually picks a plan/pack.
      const isPristine = this.dataset.gridVisualSelectionState === 'pristine';

      subtotalPrice.textContent = isPristine
        ? this.formatVariantPrice(0)
        : this.formatVariantPrice(Number(variant.price || 0) * this.getCurrentQuantity());
    }

    // Single source of truth for whether Add to cart / Buy it now can be
    // pressed. A grid-1-column merged plan/pack picker pre-checks a default
    // variant so the form is technically submittable on load, but it should
    // still read (and behave) as disabled - matching the hero search
    // Continue button - until the shopper actually picks a plan/pack.
    // isSoldOut defaults to a currentVariant-based guess for the synchronous,
    // pre-AJAX callers (a plan/pack click, before the section swap resolves);
    // handleUpdateProductInfo passes the server-confirmed value explicitly
    // once the fetch lands, which always wins.
    updateGridSubmitAvailability(isSoldOut = this.currentVariant?.available === false) {
      const formEl = document.getElementById(`product-form-${this.dataset.sectionId}`);
      const addButton = formEl?.querySelector('[name="add"]');
      const buyNowButton = formEl?.querySelector('[data-buy-now-button]');
      const dynamicCheckout = this.querySelector('.m-product-dynamic-checkout');

      const isPristine = this.dataset.gridVisualSelectionState === 'pristine';
      const showPristineStyle = isPristine && !isSoldOut;

      addButton?.classList.toggle('is-grid-pristine-disabled', showPristineStyle);
      buyNowButton?.classList.toggle('is-grid-pristine-disabled', showPristineStyle);
      dynamicCheckout?.classList.toggle('is-grid-pristine-disabled', showPristineStyle);

      if (isSoldOut) {
        this.toggleAddButton(true, window.MinimogStrings.soldOut);
      } else if (isPristine) {
        this.toggleAddButton(true);
      } else {
        this.toggleAddButton(false);
      }

      // <product-form> (theme-global.js) already derives Buy it now's disabled
      // state from the add-to-cart button's own disabled attribute, and
      // re-syncs on the variantChange pubsub event this class emits after every
      // AJAX-confirmed change. That event hasn't fired yet for the synchronous,
      // pre-AJAX callers here (a plan/pack click) - ask it to re-check now
      // instead of lagging a cycle behind Add to cart.
      formEl?.closest('product-form')?.syncBuyNowButtonState?.();
    }

    updatePriceFromLocalVariant(variant) {
      const priceContainer = this.querySelector(`#Price-${this.dataset.sectionId}`);
      const priceRoot = priceContainer?.querySelector('.m-price');

      if (!priceContainer || !priceRoot || !variant) return;

      priceContainer.classList.remove('m:hidden');

      const regularPriceNode = priceRoot.querySelector('.m-price__regular .m-price-item--regular');
      const salePriceNode = priceRoot.querySelector('.m-price__sale .m-price-item--last');
      const compareAtNode = priceRoot.querySelector('.m-price__sale s.m-price-item--regular');
      const unitWrapper = priceRoot.querySelector('.m-price__unit-wrapper');
      const unitPriceNode = priceRoot.querySelector('[data-unit-price]');
      const unitBaseNode = priceRoot.querySelector('[data-unit-price-base-unit]');
      const formattedPrice = this.formatVariantPrice(variant.price);
      const hasCompareAtPrice = Number(variant.compare_at_price) > Number(variant.price);

      if (regularPriceNode) regularPriceNode.textContent = formattedPrice;
      if (salePriceNode) salePriceNode.textContent = formattedPrice;
      if (compareAtNode && hasCompareAtPrice) {
        compareAtNode.textContent = this.formatVariantPrice(variant.compare_at_price);
      }

      const productData = this.productData;

      priceRoot.classList.toggle('m-price--on-sale', hasCompareAtPrice);
      priceRoot.classList.toggle('m-price--sold-out', !variant.available);
      priceRoot.classList.toggle(
        'm-price--no-compare',
        productData?.price_varies === false && Boolean(productData?.compare_at_price_varies)
      );

      if (unitWrapper && unitPriceNode && unitBaseNode) {
        const measurement = variant.unit_price_measurement;
        const hasUnitPrice = measurement && variant.unit_price;

        unitWrapper.classList.toggle('m:hidden', !hasUnitPrice);

        if (hasUnitPrice) {
          unitPriceNode.textContent = this.formatVariantPrice(variant.unit_price);
          unitBaseNode.textContent = `${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit || ''}`;
        }
      }

      this.updateGridSubtotal(variant);
    }

    getSelectedDaysValue(root = this.variantPicker) {
      if (!root) return '';

      const daysField = root.querySelector('.grid-1-column-days-source [data-picker-field="radio"]');
      const selectedInput = daysField?.querySelector('input[type="radio"]:checked')
        || daysField?.querySelector('input[type="radio"]:not([disabled])')
        || daysField?.querySelector('input[type="radio"]');

      return selectedInput?.value || '';
    }

    applyDaysValueToVariantPicker(variantPickerNode, daysValue) {
      if (!variantPickerNode || !daysValue) return;

      const daysField = variantPickerNode.querySelector('.grid-1-column-days-source [data-picker-field="radio"]');
      const targetInput = daysField && Array.from(daysField.querySelectorAll('input[type="radio"]')).find((input) => {
        return input.value === String(daysValue);
      });

      if (!daysField || !targetInput) return;

      daysField.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input === targetInput;
      });
      daysField.dataset.selectedValue = String(daysValue);

      const labelValue = variantPickerNode.querySelector('.grid-1-column-days-source__inner .option-label--selected');
      if (labelValue) {
        labelValue.textContent = String(daysValue);
      }
    }

    positionDaysSlider(variantPickerNode, daysValue) {
      const sliderRoot = variantPickerNode?.querySelector('[data-grid-days-slider-root]');
      const track = sliderRoot?.querySelector('[data-grid-days-track]');
      const thumb = sliderRoot?.querySelector('[data-grid-days-thumb]');
      const thumbValue = sliderRoot?.querySelector('[data-grid-days-thumb-value]');
      const progress = sliderRoot?.querySelector('[data-grid-days-progress]');

      if (!sliderRoot || !track || !thumb || !thumbValue || !progress) return;

      const minDays = Number(sliderRoot.dataset.gridDaysMin || 1);
      const maxDays = Number(sliderRoot.dataset.gridDaysMax || 30);
      const nextValue = Math.max(minDays, Math.min(maxDays, Number(daysValue || minDays)));
      const maxPosition = Math.max(track.offsetWidth - (thumb.offsetWidth * 1.125) - 2, 0);
      const percentage = maxDays > minDays ? (nextValue - minDays) / (maxDays - minDays) : 0;
      const position = maxPosition * percentage;
      const thumbRightEdge = position + thumb.offsetWidth;
      const progressWidth = Math.min(thumbRightEdge + 6, track.offsetWidth - 2);

      thumb.style.left = `${position}px`;
      thumbValue.textContent = String(nextValue);
      progress.style.width = `${progressWidth}px`;
      progress.setAttribute('data-value', String(nextValue));
      track.classList.toggle('has-value', nextValue > minDays);
    }

    positionDaysSliderWhenReady(variantPickerNode, daysValue) {
      const sliderRoot = variantPickerNode?.querySelector('[data-grid-days-slider-root]');
      const track = sliderRoot?.querySelector('[data-grid-days-track]');

      if (!sliderRoot || !track) return;

      const positionIfReady = () => {
        if (track.offsetWidth > 0) {
          this.positionDaysSlider(variantPickerNode, daysValue);
          return true;
        }

        return false;
      };

      if (positionIfReady()) return;

      let frameCount = 0;
      let resizeObserver;

      const cleanup = () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };

      const checkOnNextFrame = () => {
        if (positionIfReady()) {
          cleanup();
          return;
        }

        frameCount += 1;
        if (frameCount >= 10) {
          cleanup();
          return;
        }

        requestAnimationFrame(checkOnNextFrame);
      };

      if (typeof ResizeObserver === 'function') {
        resizeObserver = new ResizeObserver(() => {
          if (positionIfReady()) {
            cleanup();
          }
        });
        resizeObserver.observe(track);
      }

      requestAnimationFrame(checkOnNextFrame);
    }

    restoreDaysSliderState(variantPickerNode, daysValue) {
      if (!variantPickerNode || !daysValue) return;

      this.applyDaysValueToVariantPicker(variantPickerNode, daysValue);
      this.positionDaysSliderWhenReady(variantPickerNode, daysValue);
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        const daysValueToRestore = this.pendingDaysValue || this.getSelectedDaysValue();

        if (daysValueToRestore) {
          this.applyDaysValueToVariantPicker(variantPicker, daysValueToRestore);
        }

        HTMLUpdateUtility.viewTransition(
          this.variantPicker,
          variantPicker,
          this.preProcessHtmlCallbacks,
          [
            ...this.postProcessHtmlCallbacks,
            (newNode) => {
              this.restoreDaysSliderState(newNode, daysValueToRestore);
              this.pendingDaysValue = null;
            }
          ]
        );
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));

      const subtotalRoot = this.querySelector('[data-grid-subtotal]');
      if (subtotalRoot) {
        subtotalRoot.hidden = true;
      }
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider.slideTo(0);

          this.productMedia.handleSlideChange();
        }
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = this.variantGroupImagesData.mapping.find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      selectedMedias(this.initialMedias).forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });
      selectedMedias(this.initialThumbs).forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      this.bindQuantityPriceUpdates();
      this.updateGridSubtotal();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });

      this.updateGridSubtotal();
    }

    bindQuantityPriceUpdates() {
      if (this.dataset.gridQuantitySubtotalBound === 'true') return;

      this.dataset.gridQuantitySubtotalBound = 'true';

      const syncSubtotalFromInput = (input) => {
        this.syncQuantityInputs(input);
        this.updateGridSubtotal();
      };

      this.quantityInputs.forEach((input) => {
        input.addEventListener('input', () => syncSubtotalFromInput(input));
        input.addEventListener('change', () => syncSubtotalFromInput(input));

        const quantityWrapper = input.closest('m-quantity-input');
        quantityWrapper?.querySelectorAll('.m-quantity__button').forEach((button) => {
          button.addEventListener('click', () => {
            requestAnimationFrame(() => syncSubtotalFromInput(input));
          });
        });
      });
    }

    fetchQuantityRules() {
      if (!this.currentVariant.id) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
      this.updateGridSubtotal();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}

(() => {
  const PRODUCT_ICON = `
    <svg class="product-accordion-review__product-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
      <g transform="matrix(1,0,0,1,-450,0)">
        <g transform="matrix(3.108108,0,0,3.108108,424.662162,5.743243)">
          <path d="M12.174,62.5C12.174,29.205 39.205,2.174 72.5,2.174C105.795,2.174 132.826,29.205 132.826,62.5C132.826,95.795 105.795,122.826 72.5,122.826C39.205,122.826 12.174,95.795 12.174,62.5ZM55.171,14.871C37.173,21.429 23.925,37.905 22.053,57.674L45.074,57.674C45.672,40.314 49.46,25.11 54.892,15.365C54.985,15.199 55.078,15.034 55.171,14.871ZM122.947,57.674C121.075,37.905 107.827,21.429 89.829,14.871C89.922,15.034 90.015,15.199 90.108,15.365C95.54,25.11 99.328,40.314 99.926,57.674L122.947,57.674ZM89.829,110.129C107.827,103.571 121.075,87.095 122.947,67.326L99.926,67.326C99.328,84.686 95.54,99.89 90.108,109.635C90.015,109.801 89.922,109.966 89.829,110.129ZM22.053,67.326C23.925,87.095 37.173,103.571 55.171,110.129C55.078,109.966 54.985,109.801 54.892,109.635C49.46,99.89 45.672,84.686 45.074,67.326L22.053,67.326ZM90.27,57.674C89.714,42.256 86.511,28.737 81.677,20.064C80.139,17.304 78.463,15.077 76.617,13.572C75.309,12.506 73.953,11.826 72.5,11.826C71.047,11.826 69.691,12.506 68.383,13.572C66.537,15.077 64.861,17.304 63.323,20.064C58.489,28.737 55.286,42.256 54.73,57.674L90.27,57.674ZM54.73,67.326C55.286,82.744 58.489,96.263 63.323,104.936C64.861,107.696 66.537,109.923 68.383,111.428C69.691,112.494 71.047,113.174 72.5,113.174C73.953,113.174 75.309,112.494 76.617,111.428C78.463,109.923 80.139,107.696 81.677,104.936C86.511,96.263 89.714,82.744 90.27,67.326L54.73,67.326Z"></path>
        </g>
      </g>
    </svg>
  `;

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const extractReviewText = (scope, selectors) => {
    for (const selector of selectors) {
      const match = scope.querySelector(selector);
      const text = match?.textContent?.replace(/\s+/g, ' ').trim();
      if (text) return text;
    }

    return '';
  };

  const extractReviewStars = (scope) => {
    const ratingEl = scope.querySelector("[aria-label*='star'], [data-score], .jdgm-rev__rating, .jdgm-star-rating, [class*='rating']");
    const ariaLabel = ratingEl?.getAttribute('aria-label') || '';
    const ariaMatch = ariaLabel.match(/([0-9]+(?:\.[0-9]+)?)/);
    if (ariaMatch) return Math.max(0, Math.min(5, Math.round(Number(ariaMatch[1]))));

    const score = ratingEl?.getAttribute('data-score') || ratingEl?.dataset?.score;
    if (score) return Math.max(0, Math.min(5, Math.round(Number(score))));

    const filledStars = scope.querySelectorAll('.jdgm-star.jdgm--on, .jdgm-rev__rating .jdgm-star.jdgm--on').length;
    if (filledStars) return Math.max(0, Math.min(5, filledStars));

    return 0;
  };

  const parseJudgeMeReviews = (markup) => {
    if (!markup) return [];

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const reviewNodes = Array.from(doc.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__review'));

    return reviewNodes.map((reviewEl) => ({
      title: extractReviewText(reviewEl, [
        '.jdgm-rev__title',
        '.jdgm-rev-widg__title',
        '[class*="review-title"]'
      ]),
      body: extractReviewText(reviewEl, [
        '.jdgm-rev__body',
        '.jdgm-rev__content-body',
        '.jdgm-rev-widg__body',
        'p'
      ]),
      author: extractReviewText(reviewEl, [
        '.jdgm-rev__author',
        '.jdgm-rev-widg__author',
        '.jdgm-reviewer-name'
      ]),
      reviewerLocation: extractReviewText(reviewEl, [
        '.jdgm-rev__location',
        '.jdgm-rev__reviewer-location',
        '.jdgm-rev-widg__location',
        '[class*="reviewer-location"]',
        '[class*="rev__location"]'
      ]),
      productName: extractReviewText(reviewEl, [
        '.jdgm-rev__prod-link',
        '.jdgm-rev__product',
        '.jdgm-product-name',
        '.jdgm-rev__prod-info-wrapper a'
      ]),
      stars: extractReviewStars(reviewEl)
    })).filter((review) => review.body || review.title || review.author);
  };

  const normalizeReviewProductName = (value) => String(value || '')
    .toLowerCase()
    .replace(/&amp;/g, '&')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const filterReviewsForCurrentProduct = (reviews, root, sourceConfig = null) => {
    if (!Array.isArray(reviews) || !reviews.length) return [];
    if (sourceConfig?.reviewsSelection !== 'current_product') return reviews;

    const currentProductTitle = normalizeReviewProductName(root.dataset.productTitle || '');
    if (!currentProductTitle) return reviews;

    const matchedReviews = reviews.filter((review) => {
      const reviewProductName = normalizeReviewProductName(review.productName || '');
      return reviewProductName && reviewProductName === currentProductTitle;
    });

    return matchedReviews.length ? matchedReviews : reviews;
  };

  const renderReviewStars = (count) => Array.from({ length: 5 }, (_, index) => {
    const filled = index < count;
    return `<span class="product-accordion-review__star${filled ? ' is-filled' : ''}" aria-hidden="true">★</span>`;
  }).join('');

  const renderReviewList = (root, reviews, sourceConfig = null) => {
    const list = root.querySelector('[data-reviews-list]');
    const status = root.querySelector('[data-reviews-status]');
    const fallbackProductTitle = root.dataset.productTitle || '';
    if (!list || !status) return;

    list.innerHTML = reviews.map((review) => {
      const productName = review.productName || fallbackProductTitle;
      const metaContext = productName
        ? `<div class="product-accordion-review__product">${PRODUCT_ICON}<span>${escapeHtml(productName)}</span></div>`
        : review.reviewerLocation
          ? `<div class="product-accordion-review__location">${PRODUCT_ICON}<span>${escapeHtml(review.reviewerLocation)}</span></div>`
          : '';

      return `
        <article class="product-accordion-review">
          <div class="product-accordion-review__meta">
            <div class="product-accordion-review__stars" aria-label="${review.stars} out of 5 stars">
              ${renderReviewStars(review.stars)}
            </div>
            ${review.author ? `<div class="product-accordion-review__author">${escapeHtml(review.author)}</div>` : ''}
            ${metaContext}
          </div>
          <div class="product-accordion-review__content">
            ${review.title ? `<h4 class="product-accordion-review__title">${escapeHtml(review.title)}</h4>` : ''}
            ${review.body ? `<p class="product-accordion-review__body">${escapeHtml(review.body)}</p>` : ''}
          </div>
        </article>
      `;
    }).join('');

    status.hidden = true;
    list.hidden = false;
  };

  const renderEmptyReviewList = (root) => {
    const list = root.querySelector('[data-reviews-list]');
    const status = root.querySelector('[data-reviews-status]');
    if (list) {
      list.innerHTML = '';
      list.hidden = true;
    }
    if (status) {
      status.textContent = 'No reviews yet for this product.';
      status.hidden = false;
    }
  };

  const getJudgeMeBlockId = (sourceRoot) => {
    if (!sourceRoot) return '';

    return (
      sourceRoot.getAttribute('data-jdgm-block-id')
      || sourceRoot.querySelector?.('[data-jdgm-block-id]')?.getAttribute('data-jdgm-block-id')
      || ''
    );
  };

  const extractJudgeMeSourceConfig = (sourceRoot, root) => {
    const blockId = getJudgeMeBlockId(sourceRoot);
    const shopDomain = root.dataset.shopDomain || window.Shopify?.shop || '';
    const fallbackConfig = {
      reviewsSelection: 'current_product',
      productId: root.dataset.productId || '',
      collectionId: '',
      productIdsRaw: '',
      customTagsRaw: '',
      tagFilterRaw: '',
      maxReviews: 20,
      reviewsShown: Number(root.dataset.reviewsShown || 3),
      platform: 'shopify',
      shopDomain
    };

    if (!blockId) return fallbackConfig;

    const scripts = Array.from(document.querySelectorAll('script'));
    const configScript = scripts.find((script) => (script.textContent || '').includes(`const blockId = "${blockId}"`));
    const scriptText = configScript?.textContent || '';

    if (!scriptText) return fallbackConfig;

    const extractString = (key) => {
      const match = scriptText.match(new RegExp(`${key}:\\s*"([^"]*)"`, 'i'));
      return match ? match[1] : '';
    };

    const extractNumber = (key) => {
      const match = scriptText.match(new RegExp(`${key}:\\s*'?([0-9]+(?:\\.[0-9]+)?)'?`, 'i'));
      return match ? Number(match[1]) : null;
    };

    return {
      reviewsSelection: extractString('reviews_selection') || fallbackConfig.reviewsSelection,
      collectionId: extractString('collection_id'),
      productId: extractString('product_id') || fallbackConfig.productId,
      productIdsRaw: extractString('product_ids'),
      customTagsRaw: extractString('custom_tags'),
      tagFilterRaw: extractString('tag_filter'),
      maxReviews: extractNumber('max_reviews') || fallbackConfig.maxReviews,
      reviewsShown: extractNumber('reviews_shown') || fallbackConfig.reviewsShown,
      platform: extractString('platform') || fallbackConfig.platform,
      shopDomain: extractString('shop_domain') || fallbackConfig.shopDomain
    };
  };

  const buildJudgeMeApiQuery = (root, sourceConfig = null) => {
    const reviewsShown = Number(root.dataset.reviewsShown || 3);
    const config = sourceConfig || extractJudgeMeSourceConfig(null, root);
    if (!config?.shopDomain) return null;

    const params = new URLSearchParams({
      widget_type: 'cards_carousel',
      page: '1',
      per_page: String(Math.max(config.maxReviews || 20, config.reviewsShown || reviewsShown, 3)),
      sort_by: 'created_at',
      sort_dir: 'desc',
      shop_domain: config.shopDomain,
      platform: config.platform || 'shopify'
    });

    const parseJsonArray = (rawValue) => {
      if (!rawValue) return [];
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const reviewsSelection = config.reviewsSelection || 'all';
    if (reviewsSelection === 'current_product' && config.productId) {
      params.set('product_id', config.productId);
    } else if (reviewsSelection === 'current_collection' && config.collectionId) {
      params.set('collection_id', config.collectionId);
    } else if (reviewsSelection === 'custom_products') {
      parseJsonArray(config.productIdsRaw).forEach((productId) => {
        params.append('select_products[]', String(productId));
      });
    } else if (reviewsSelection === 'featured') {
      params.set('featured_only', 'true');
    } else if (reviewsSelection === 'store_reviews') {
      params.set('review_type', 'store_reviews');
    } else if (reviewsSelection === 'product_reviews') {
      params.set('review_type', 'product_reviews');
    }

    parseJsonArray(config.customTagsRaw).forEach((tag) => {
      params.append('custom_tags[]', String(tag));
    });

    parseJsonArray(config.tagFilterRaw).forEach((tag) => {
      params.append('tag_filter[]', String(tag));
    });

    return params;
  };

  const findProductReviewsAppSource = () => {
    const selectors = [
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-carousel-wrapper',
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-carousel',
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-carousel__wrapper',
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-widget[data-widget-name*="carousel"]',
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-widget[data-widget*="carousel"]',
      '.m-app-section.product-reviews-source .shopify-app-block [class*="jdgm"][class*="carousel"]',
      '.m-app-section.product-reviews-source .shopify-app-block .jdgm-widget',
      '.m-app-section.product-reviews-source .jdgm-widget'
    ];

    return document.querySelector(selectors.join(', '));
  };

  const fetchJudgeMeReviews = async (root, sourceConfig = null) => {
    const params = buildJudgeMeApiQuery(root, sourceConfig);
    if (!params || typeof fetch !== 'function') return [];

    const apiHost = window.jdgm?.API_HOST || 'https://judge.me/api/v1/widgets/';
    const baseUrl = apiHost.endsWith('/') ? apiHost : `${apiHost}/`;
    const response = await fetch(`${baseUrl}reviews/all_reviews_js_based?${params.toString()}`, {
      credentials: 'omit'
    });

    if (!response.ok) {
      throw new Error(`Judge.me API request failed with ${response.status}`);
    }

    const payload = await response.json();
    return parseJudgeMeReviews(payload?.html || '');
  };

  const syncProductAccordionReviews = async (root) => {
    const source = findProductReviewsAppSource();
    const reviewsShown = Number(root.dataset.reviewsShown || 3);
    const sourceConfig = extractJudgeMeSourceConfig(source, root);
    const sourceReviews = filterReviewsForCurrentProduct(
      parseJudgeMeReviews(source?.innerHTML || ''),
      root,
      sourceConfig
    ).slice(0, reviewsShown);

    try {
      const fetchedReviews = filterReviewsForCurrentProduct(
        await fetchJudgeMeReviews(root, sourceConfig),
        root,
        sourceConfig
      ).slice(0, reviewsShown);
      if (fetchedReviews.length) {
        renderReviewList(root, fetchedReviews, sourceConfig);
        return;
      }
    } catch (error) {
    }

    if (sourceReviews.length) {
      renderReviewList(root, sourceReviews, sourceConfig);
      return;
    }

    renderEmptyReviewList(root);
  };

  const initProductAccordionReviews = () => {
    document.querySelectorAll('[data-product-accordion-reviews]').forEach((root) => {
      if (root.dataset.reviewsInitialized === 'true') return;
      root.dataset.reviewsInitialized = 'true';

      syncProductAccordionReviews(root);

      const source = findProductReviewsAppSource();
      if (!source) return;

      const observer = new MutationObserver(() => {
        syncProductAccordionReviews(root);
      });

      observer.observe(source, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductAccordionReviews, { once: true });
  } else {
    initProductAccordionReviews();
  }
})();
